import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import type { RootState } from '../../../../configuration/setup/store';
import { getDiscounts } from '../../common/discount/redux/discount.redux';
import { getTotalPrices } from '../../common/price/priceService';
import { getEstimatedPrice } from '../../common/price/priceUtil';
import { TotalPrice } from '../../common/price/TotalPrice';
import type { Period } from '../../common/termedLevel/types';
import { formatPrice } from '../../common/utils/currencyUtil';
import { DATE_FORMAT } from '../../common/utils/dateUtil';
import { formatDurationUnit } from '../../common/utils/formatDurationUnit';
import { getCurrencyOfSelectedProduct } from '../CheckoutService';
import { getRenewsToProductLevel, getSelectedProductOrProductLevel } from '../redux/checkout.redux';
import type { BookableProduct, ProductLevel } from '../redux/types';
import { getSelectedResourceIds } from '../resourceSelection/redux/resourceSelection.redux';
import { PriceDetailsContainer } from './PriceDetails';

interface Props {
    totalPrice: number;
    currency: string | undefined;
    futurePrices?: Array<{ totalPrice: number; startDate: Date }>;
    contractPeriod: Period | undefined;
    selectedProduct: BookableProduct;
    renewsToProductLevel?: ProductLevel;
}

const formatContractPeriod = (contractPeriod: Period, intl: ReturnType<typeof useIntl>) =>
    `${contractPeriod.length} ${formatDurationUnit(contractPeriod.unit, intl, contractPeriod.length)}`;

const Price = ({
    totalPrice,
    currency,
    futurePrices,
    contractPeriod,
    selectedProduct,
    renewsToProductLevel,
}: Props) => {
    const intl = useIntl();
    if (selectedProduct.price === 0) {
        return (
            <div className='margin-left-20-sm margin-top-20-xs margin-top-20-ls margin-top-0-sm'>
                <div className='text-right'>
                    <span className='text-size-14 line-height-20 text-medium label label-success label-filled'>
                        <FormattedMessage id={'intl-msg:marketplace.free'} />
                    </span>
                </div>
            </div>
        );
    }
    return (
        <div className='margin-left-20-sm margin-right-10-sm margin-top-20-xs margin-top-20-ls margin-top-0-sm'>
            <PriceDetailsContainer selectedProduct={selectedProduct} />
            <hr className='margin-top-3 margin-bottom-3' />
            <div className='text-right'>
                <TotalPrice totalPrice={totalPrice} currency={currency} />
            </div>
            <div className='text-right text-size-14'>
                {futurePrices?.map((futurePrice, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: Future prices array is stable and items have no unique IDs
                    <div key={index}>
                        <FormattedMessage
                            id={'intl-msg:marketplace.from'}
                            values={{
                                date: intl.formatDate(futurePrice.startDate, DATE_FORMAT),
                            }}
                        />
                        {': '}
                        {formatPrice(intl, futurePrice.totalPrice, currency)}{' '}
                        <FormattedMessage id={'intl-msg:marketplace.productDetails.price.rate.total'} />
                    </div>
                ))}
            </div>
            {contractPeriod !== undefined && (
                <div className='text-right'>
                    <FormattedMessage id='intl-msg:common-message.contracts.period' /> {contractPeriod.length}{' '}
                    {formatDurationUnit(contractPeriod.unit, intl, contractPeriod.length)}
                </div>
            )}
            {renewsToProductLevel?.contractPeriod !== undefined && (
                <div className='text-size-12 padding-y-5 line-height-14 text-wrap-balance text-right text-italic'>
                    <FormattedMessage
                        id='intl-msg:marketplace.checkout.automaticRenewal'
                        values={{
                            contractPeriod: formatContractPeriod(renewsToProductLevel.contractPeriod, intl),
                            price: formatPrice(
                                intl,
                                getEstimatedPrice(renewsToProductLevel.price),
                                renewsToProductLevel.currency
                            ),
                        }}
                    />
                </div>
            )}
        </div>
    );
};

const mapStateToPropsCommon = (state: RootState, selectedProduct: BookableProduct) => {
    const discounts = getDiscounts(state);
    const contractPeriod = selectedProduct?.contractPeriod;
    const selectedResourceIds = getSelectedResourceIds(state);

    const { totalPrice, futurePrices } = getTotalPrices(discounts, selectedProduct, selectedResourceIds);

    return {
        totalPrice,
        currency: getCurrencyOfSelectedProduct(state),
        futurePrices,
        contractPeriod,
        selectedProduct,
        renewsToProductLevel: getRenewsToProductLevel(state),
    };
};

const mapStateToPropsForCurrentPrice = (state: RootState) => {
    const selectedProduct = getSelectedProductOrProductLevel(state);
    return mapStateToPropsCommon(state, selectedProduct);
};

const mapStateToPropsForFuturePrice = (state: RootState) => {
    const selectedProduct = getSelectedProductOrProductLevel(state);
    const futurePriceSelectedProduct = { ...selectedProduct, price: selectedProduct?.futurePrice?.price ?? 0 };

    return mapStateToPropsCommon(state, futurePriceSelectedProduct);
};
export const PriceContainer = connect(mapStateToPropsForCurrentPrice)(Price);
export const FuturePriceContainer = connect(mapStateToPropsForFuturePrice)(Price);
